/**
 * Admin API client methods.
 * Wraps apiGet/apiPost for admin endpoints.
 */

import { apiFetch, apiGet, apiPost } from '@/services/apiClient';

// ============================================================================
// TYPES
// ============================================================================

export interface PlanData {
  id: string;
  name: string;
  slug: string;
  priceMonthly: number;
  priceQuarterly: number;
  priceYearly: number;
  carouselLimit: number;
  generationLimit: number;
  exportLimit: number;
  accessDurationDays: number | null;
  features: string[];
  sortOrder: number;
  isActive: boolean;
  isPublic: boolean;
  highlightColor: string | null;
  externalIdMercadopago: string | null;
  externalIdPagarme: string | null;
  checkoutUrl: string | null;
  externalIdOnprofit: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AdminUserData {
  id: string;
  email: string;
  display_name: string;
  handle: string;
  role: string;
  created_at: string;
  subscription: {
    id: string;
    planName: string;
    planSlug: string;
    status: string;
  } | null;
}

// ============================================================================
// ADMIN ROLE CHECK
// ============================================================================

export const checkAdminRole = () =>
  apiGet<{ isAdmin: boolean }>('/api/admin/check-role');

// ============================================================================
// PLANS
// ============================================================================

export const getPlans = () =>
  apiGet<{ plans: PlanData[] }>('/api/admin/plans');

export const createPlan = (data: {
  name: string;
  slug: string;
  priceMonthly: number;
  priceQuarterly?: number;
  priceYearly: number;
  carouselLimit?: number;
  generationLimit?: number;
  exportLimit?: number;
  accessDurationDays?: number | null;
  features?: string[];
  sortOrder?: number;
  isActive?: boolean;
  isPublic?: boolean;
  highlightColor?: string | null;
  checkoutUrl?: string | null;
  externalIdOnprofit?: string | null;
}) => apiPost<{ plan: PlanData }>('/api/admin/plans/create', data);

export const updatePlan = (data: {
  id: string;
  name?: string;
  slug?: string;
  priceMonthly?: number;
  priceQuarterly?: number;
  priceYearly?: number;
  carouselLimit?: number;
  generationLimit?: number;
  exportLimit?: number;
  accessDurationDays?: number | null;
  features?: string[];
  sortOrder?: number;
  isActive?: boolean;
  isPublic?: boolean;
  highlightColor?: string | null;
  checkoutUrl?: string | null;
  externalIdOnprofit?: string | null;
}) => apiPost<{ plan: PlanData }>('/api/admin/plans/update', data);

export const togglePlan = (id: string, active: boolean) =>
  apiPost<{ success: boolean }>('/api/admin/plans/toggle', { id, active });

export const deletePlan = (id: string) =>
  apiPost<{ success: boolean }>('/api/admin/plans/delete', { id });

// ============================================================================
// USERS
// ============================================================================

export const getUsers = (params?: Record<string, string>) =>
  apiGet<{ users: AdminUserData[]; total: number; page: number; limit: number }>(
    '/api/admin/users',
    params,
  );

export const createUser = (data: {
  email: string;
  password?: string;
  displayName?: string;
  planId?: string;
}) => apiPost<{ user: { id: string; email: string }; generatedPassword?: string }>(
  '/api/admin/users/create',
  data,
);

// ============================================================================
// SUBSCRIPTIONS
// ============================================================================

export const changeUserPlan = (userId: string, planId: string) =>
  apiPost<{ success: boolean }>('/api/admin/subscriptions/change', { userId, planId });

export const cancelUserSubscription = (subscriptionId: string) =>
  apiPost<{ success: boolean }>('/api/admin/subscriptions/cancel', { subscriptionId });

// ============================================================================
// LEADS EXPORT
// ============================================================================

export interface LeadExportFilters {
  plan?: string;
  subscriptionStatus?: string;
  onboardingCompleted?: string;
  faturamento?: string;
  objetivoCarrosseis?: string;
  tipoConteudo?: string;
  leadsInstagram?: string;
  channel?: string;
  hasCarousel?: string;
  hasPaid?: string;
  dateFrom?: string;
  dateTo?: string;
}

export interface LeadPreviewData {
  email: string;
  display_name: string;
  handle: string;
  phone: string;
  plan: string;
  plan_slug: string;
  subscription_status: string;
  onboarding_completed: string;
  objetivo_carrosseis: string;
  tipo_conteudo: string;
  leads_instagram: string;
  faturamento: string;
  first_touch_channel: string;
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  country: string;
  language: string;
  created_carousel: string;
  has_paid: string;
  created_at: string;
}

function buildLeadsQueryString(filters: LeadExportFilters, format: string): string {
  const params = new URLSearchParams();
  params.set('format', format);
  for (const [key, value] of Object.entries(filters)) {
    if (value) params.set(key, value);
  }
  return params.toString();
}

export const getLeadsPreview = (filters: LeadExportFilters) =>
  apiGet<{ leads: LeadPreviewData[]; total: number }>(
    '/api/admin/leads/export',
    { ...filters, format: 'json' } as Record<string, string>,
  );

export const downloadLeadsCsv = (filters: LeadExportFilters) => {
  const qs = buildLeadsQueryString(filters, 'csv');
  const baseUrl = import.meta.env.VITE_API_URL || '';
  window.open(`${baseUrl}/api/admin/leads/export?${qs}`, '_blank');
};

// ============================================================================
// SUPPORT TOOLS (user detail + account actions + audit log)
// ============================================================================

export type InstagramField = 'avatar' | 'name' | 'bio' | 'handle';
export type AdminActionStatus = 'success' | 'noop' | 'dry_run';

export interface AccessDiagnostic {
  codigo: string;
  severidade: 'alta' | 'media' | 'info';
  mensagem: string;
}

export interface AdminAuditLog {
  id: number;
  actor_type: 'admin' | 'support_api';
  actor_id: string | null;
  actor_key_id: string | null;
  target_user_id: string;
  action: string;
  before: unknown;
  after: unknown;
  metadata: Record<string, unknown> | null;
  status: 'success' | 'error' | 'noop' | 'dry_run';
  error: string | null;
  created_at: string;
}

export interface AdminUserSubscription {
  id: string;
  plan_id: string;
  status: string;
  period: string | null;
  created_at: string;
  current_period_start: string | null;
  current_period_end: string | null;
  canceled_at: string | null;
  payment_provider: string | null;
  external_subscription_id: string | null;
  plan: { name: string | null; slug: string | null; access_duration_days: number | null; carousel_limit: number | null; generation_limit: number | null } | null;
}

export interface EmailAlias {
  email: string;
  createdAt: string;
  source: 'admin_email_change' | 'support_email_change' | 'backfill';
}

export interface AdminUserDetail {
  auth: {
    id: string;
    email: string | null;
    created_at: string | null;
    last_sign_in_at: string | null;
    email_confirmed_at: string | null;
    /** Senha temporária gerada pelo suporte ainda não trocada pelo usuário. */
    must_change_password?: boolean;
  };
  profile: {
    id: string;
    display_name: string | null;
    handle: string | null;
    avatar_url: string | null;
    preferences: Record<string, any> | null;
    instagram_locked: boolean | null;
    role: string | null;
    trial_used: boolean | null;
    paywall_active: boolean | null;
    plan: string | null;
  } | null;
  planStatus: {
    planName: string;
    canCreateCarousel: boolean;
    canGenerate: boolean;
    limits: { carousel_limit: number; generation_limit: number; daily_carousel_limit: number | null };
    usage: { carousels: number; generations: number };
    dailyCarouselUsage: number;
  } | null;
  subscriptions: AdminUserSubscription[];
  invoices: Array<{ id: string; plan_name: string; amount: number; period: string; status: string; paid_at: string | null; created_at: string }>;
  onprofitEvents: Array<{ order_id: string; email?: string | null; status: string | null; item_type: string | null; price_cents: number | null; created_at: string }>;
  /** E-mails anteriores: compras feitas com eles são vinculadas a esta conta. */
  emailAliases?: EmailAlias[];
  auditLogs: AdminAuditLog[];
  diagnosticos: AccessDiagnostic[];
  partialErrors: string[];
}

export interface InstagramRefreshResult {
  status: AdminActionStatus;
  handle: string;
  changed: InstagramField[];
  before: { display_name?: string; avatar_url?: string; biography?: string; handle?: string };
  after: { display_name?: string; avatar_url?: string; biography?: string; handle?: string };
  warnings: string[];
}

export const getAdminUserDetail = (userId: string) =>
  apiGet<AdminUserDetail>('/api/admin/users/detail', { userId });

// The Apify profile scraper polls for up to 90s.
const INSTAGRAM_TIMEOUT_MS = 150_000;

export const refreshUserInstagram = (data: { userId: string; fields: InstagramField[]; dryRun: boolean; handle?: string }) =>
  apiPost<InstagramRefreshResult>('/api/admin/users/instagram/refresh', data, INSTAGRAM_TIMEOUT_MS);

export const updateUserProfile = (data: { userId: string; displayName?: string; handle?: string; avatarUrl?: string }) =>
  apiPost<{ status: 'success' | 'noop' }>('/api/admin/users/profile', data);

export const relinkUserInstagram = (data: { userId: string; newHandle: string; refresh: boolean }) =>
  apiPost<{ status: 'success' | 'noop'; handle: string; refresh?: InstagramRefreshResult | { error: string; code: string } }>(
    '/api/admin/users/instagram/relink',
    data,
    INSTAGRAM_TIMEOUT_MS,
  );

export const unlinkUserInstagram = (data: { userId: string; clearAvatar: boolean }) =>
  apiPost<{ status: 'success' | 'noop' }>('/api/admin/users/instagram/unlink', data);

export const changeUserEmail = (data: { userId: string; newEmail: string; confirm?: boolean }) =>
  apiPost<{ outcome: 'alterado' | 'igual' | 'dry_run' | 'pendente_confirmacao' }>('/api/admin/users/email', data);

export const removeEmailAlias = (data: { userId: string; email: string }) =>
  apiFetch<{ status: 'success' }>('/api/admin/users/email-alias', { method: 'DELETE', body: JSON.stringify(data) });

export const resetUserPassword = (data: { userId: string; mode: 'link' | 'temporary' }) =>
  apiPost<{ mode: 'link'; sent: true } | { mode: 'temporary'; temporaryPassword: string }>(
    '/api/admin/users/password-reset',
    data,
  );

export const getAdminAuditLogs = (params: Record<string, string>) =>
  apiGet<{ logs: AdminAuditLog[]; total: number; page: number; limit: number }>('/api/admin/audit-logs', params);

// ============================================================================
// IMPERSONATE ("entrar como usuário")
// ============================================================================

export interface ImpersonationStartResult {
  token: string;
  sessionId: string;
  expiresAt: string;
  target: { id: string; email: string | null; displayName: string | null };
}

export interface AdminImpersonationSession {
  id: string;
  adminId: string;
  targetUserId: string;
  reason: string;
  createdAt: string;
  expiresAt: string;
}

export const startImpersonation = (data: { userId: string; reason: string }) =>
  apiPost<ImpersonationStartResult>('/api/admin/impersonation/start', data);

export const endAdminImpersonation = (sessionId: string) =>
  apiPost<{ ended: boolean }>('/api/admin/impersonation/end', { sessionId });

export const getActiveImpersonation = () =>
  apiGet<{ session: AdminImpersonationSession | null }>('/api/admin/impersonation/active');
